import {
  CHANGE_READINESS,
  SCORE_KEYS,
  WORK_LOCATION_PREFERENCES,
  type CandidateFilters,
  type ChangeReadiness,
  type ScoreKey,
  type WorkLocationPreference,
} from "@vidario/domain";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

/** URL-param prefix for every filter key. Keeps query strings readable
 * and namespaced so we don't collide with the route's existing `tab` /
 * `sort` / `search` params. */
const PARAM = {
  minScore: "f_score",
  minVorauswahl: "f_vor",
  maxDistance: "f_dist",
  mustHavesOnly: "f_must",
  salaryMin: "f_smin",
  salaryMax: "f_smax",
  workLocations: "f_loc",
  changeReadiness: "f_change",
  // Sub-scores get one param each so the URL stays declarative.
  subScore: (k: ScoreKey) => `f_${k}`,
} as const;

/** URL-state hook for the candidate filter set. The URL is the single
 * source of truth — no local draft state — which keeps back/forward
 * navigation correct without a sync effect (which the lint rule
 * `react-hooks/set-state-in-effect` blocks). Slider responsiveness
 * during drag stays smooth because Radix Slider holds its own internal
 * value while pointing the controlled value at the URL-derived one. */
export function useCandidateFilters(): {
  filters: CandidateFilters;
  setFilters: (next: CandidateFilters) => void;
} {
  const [params, setParams] = useSearchParams();

  // `params.toString()` is stable when no filter param changes — keeps
  // downstream memos from invalidating on unrelated URL flips
  // (`tab`, `sort`, `search`).
  const filters = useMemo(() => readFromParams(params), [params]);

  const setFilters = useCallback(
    (next: CandidateFilters) => {
      setParams((prev) => writeToParams(prev, next), { replace: true });
    },
    [setParams],
  );

  return { filters, setFilters };
}

export function readFromParams(params: URLSearchParams): CandidateFilters {
  const result: CandidateFilters = {};

  const minScore = readInt(params, PARAM.minScore, MINIMUM);
  if (minScore !== undefined) result.minScore = minScore;
  const minVorauswahl = readInt(params, PARAM.minVorauswahl, MINIMUM);
  if (minVorauswahl !== undefined) result.minVorauswahl = minVorauswahl;

  const maxDistance = readInt(params, PARAM.maxDistance, NON_NEGATIVE);
  if (maxDistance !== undefined) result.maxDistanceKm = maxDistance;

  if (params.get(PARAM.mustHavesOnly) === "1") result.mustHavesOnly = true;

  const salaryMin = readInt(params, PARAM.salaryMin, NON_NEGATIVE);
  if (salaryMin !== undefined) result.salaryMin = salaryMin;
  const salaryMax = readInt(params, PARAM.salaryMax, NON_NEGATIVE);
  if (salaryMax !== undefined) result.salaryMax = salaryMax;

  const workLocs = (params.get(PARAM.workLocations) ?? "")
    .split(",")
    .filter((v): v is WorkLocationPreference =>
      (WORK_LOCATION_PREFERENCES as readonly string[]).includes(v),
    );
  if (workLocs.length > 0) result.workLocations = workLocs;

  const readiness = (params.get(PARAM.changeReadiness) ?? "")
    .split(",")
    .filter((v): v is ChangeReadiness => (CHANGE_READINESS as readonly string[]).includes(v));
  if (readiness.length > 0) result.changeReadiness = readiness;

  const minSubScores: Partial<Record<ScoreKey, number>> = {};
  for (const key of SCORE_KEYS) {
    const v = readInt(params, PARAM.subScore(key), PERCENT);
    if (v !== undefined) minSubScores[key] = v;
  }
  if (Object.keys(minSubScores).length > 0) result.minSubScores = minSubScores;

  return result;
}

export function writeToParams(prev: URLSearchParams, filters: CandidateFilters): URLSearchParams {
  const next = new URLSearchParams(prev);
  // A different filter is a different result: start again at page 1 (#14).
  next.delete("page");
  // Drop all known filter keys first, then re-set only the active ones.
  // Keeps the URL minimal and lets the caller pass {} to reset.
  for (const k of [
    PARAM.minScore,
    PARAM.minVorauswahl,
    PARAM.maxDistance,
    PARAM.mustHavesOnly,
    PARAM.salaryMin,
    PARAM.salaryMax,
    PARAM.workLocations,
    PARAM.changeReadiness,
  ]) {
    next.delete(k);
  }
  for (const key of SCORE_KEYS) next.delete(PARAM.subScore(key));

  if (typeof filters.minScore === "number" && filters.minScore > 0) {
    next.set(PARAM.minScore, String(filters.minScore));
  }
  if (typeof filters.minVorauswahl === "number" && filters.minVorauswahl > 0) {
    next.set(PARAM.minVorauswahl, String(filters.minVorauswahl));
  }
  if (typeof filters.maxDistanceKm === "number") {
    next.set(PARAM.maxDistance, String(filters.maxDistanceKm));
  }
  if (filters.mustHavesOnly) next.set(PARAM.mustHavesOnly, "1");
  if (typeof filters.salaryMin === "number") {
    next.set(PARAM.salaryMin, String(filters.salaryMin));
  }
  if (typeof filters.salaryMax === "number") {
    next.set(PARAM.salaryMax, String(filters.salaryMax));
  }
  if (filters.workLocations && filters.workLocations.length > 0) {
    next.set(PARAM.workLocations, filters.workLocations.join(","));
  }
  if (filters.changeReadiness && filters.changeReadiness.length > 0) {
    next.set(PARAM.changeReadiness, filters.changeReadiness.join(","));
  }
  if (filters.minSubScores) {
    for (const key of SCORE_KEYS) {
      const v = filters.minSubScores[key];
      if (typeof v === "number" && v > 0) {
        next.set(PARAM.subScore(key), String(v));
      }
    }
  }
  return next;
}

const PERCENT = { min: 0, max: 100 } as const;
// A minimum of 0 filters nothing and gets no chip to remove it by, so it
// would only show up in the counts (#201).
const MINIMUM = { min: 1, max: 100 } as const;
const NON_NEGATIVE = { min: 0, max: Number.MAX_SAFE_INTEGER } as const;

/** A value outside the range counts as unset: the server would reject it (400),
 * and a typed "-5" or a hand-edited URL must not empty the candidate page. */
function readInt(
  params: URLSearchParams,
  key: string,
  range: { min: number; max: number },
): number | undefined {
  const raw = params.get(key);
  if (raw === null) return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= range.min && n <= range.max ? n : undefined;
}
